// Antares fixtures — turn each canned Antares run into the shape of a real Resolution Center scan
// so `/repositories/:repoId/scans/:scanId` can render one against MSW without any RC-page changes.
//
// The RC page treats `detector` as an opaque tool identifier. Antares scans wire it as `codeql`
// (a value that already lives in `ToolSchema`) so we do not have to touch `ToolSchema` or the
// OpenAPI parity check — display-name / icon overrides are handled elsewhere.

import invariant from 'tiny-invariant'
import {
  antaresRunCatalog,
  getAntaresFindingOverlayContentForSlug,
  getAntaresFindingOverlayForSlug,
  getAntaresScorecardForSlug,
  getAntaresThreatModelContentForSlug,
  getAntaresThreatModelForSlug,
  getCodetfFixResultForFindingId,
  getCodetfForSlug,
  type AntaresFinding,
  type CodeTFChangeSet,
  type CodeTFFixResult,
  type CodeTFRating,
  type RunReport,
} from '../src/mock-data/antares-scans/index.ts'
import type {
  AnalysisPaginatedResponse,
  AnalysisResponse,
  ChangesetPaginatedResponse,
  Confidence,
  DimensionAnswer,
  DimensionAnswerEvidence,
  DimensionScore,
  FindingAggregateResponse,
  FindingOverlayCluster,
  FindingOverlayPriority,
  FindingOverlayResponse,
  FindingOverlaysResponse,
  FindingResponse,
  FindingResultResponse,
  FixOutcome,
  QualityResponse,
  RatingResponse,
  Repository,
  ScanResponse,
  ScorecardPayload,
  ScorecardsResponse,
  Severity,
  StrategyResponse,
  ThreatModelContent,
  ThreatModelResponse,
} from '../src/utils/api-client/api-schemas'
import {
  FakeFindingWithResults,
  FakePaginatedFindingsWithResults,
  FakeRepository,
  FakeResult,
  FakeScan,
  FakeScanAnalysis,
} from './models'

export const ANTARES_SLUGS = ['webgoat', 'juice-shop', 'roller', 'airflow'] as const
export type AntaresSlug = (typeof ANTARES_SLUGS)[number]

const catalogBySlug = new Map(antaresRunCatalog.map(entry => [entry.slug, entry]))

const requireRun = (slug: string): RunReport => {
  const entry = catalogBySlug.get(slug)
  invariant(entry, `Unknown Antares slug: ${slug}`)
  return entry.report
}

// Scan IDs are the report's own run_id (already UUID-shaped). Repo IDs are opaque per-slug strings —
// the RC page treats repository IDs as opaque, so a stable slug-derived identifier is sufficient.
const SCAN_ID_BY_SLUG: Record<AntaresSlug, string> = {
  webgoat: requireRun('webgoat').run_id,
  'juice-shop': requireRun('juice-shop').run_id,
  roller: requireRun('roller').run_id,
  airflow: requireRun('airflow').run_id,
}

const REPO_ID_BY_SLUG: Record<AntaresSlug, string> = {
  webgoat: 'antares-repo-webgoat',
  'juice-shop': 'antares-repo-juice-shop',
  roller: 'antares-repo-roller',
  airflow: 'antares-repo-airflow',
}

const ANALYSIS_ID_BY_SLUG: Record<AntaresSlug, string> = {
  webgoat: 'antares-analysis-webgoat',
  'juice-shop': 'antares-analysis-juice-shop',
  roller: 'antares-analysis-roller',
  airflow: 'antares-analysis-airflow',
}

export const ANTARES_SCAN_IDS: readonly string[] = ANTARES_SLUGS.map(slug => SCAN_ID_BY_SLUG[slug])
export const ANTARES_REPO_IDS: readonly string[] = ANTARES_SLUGS.map(slug => REPO_ID_BY_SLUG[slug])
export const ANTARES_ANALYSIS_IDS: readonly string[] = ANTARES_SLUGS.map(slug => ANALYSIS_ID_BY_SLUG[slug])

const SCAN_ID_TO_SLUG = new Map(ANTARES_SLUGS.map(slug => [SCAN_ID_BY_SLUG[slug], slug]))
const REPO_ID_TO_SLUG = new Map(ANTARES_SLUGS.map(slug => [REPO_ID_BY_SLUG[slug], slug]))
const ANALYSIS_ID_TO_SLUG = new Map(ANTARES_SLUGS.map(slug => [ANALYSIS_ID_BY_SLUG[slug], slug]))

// Every Antares finding UUID is unique across runs, so we can resolve a finding back to its slug
// without needing a scan ID from the caller. This keeps the single-finding + single-result handlers
// pathless of any scan-context lookup.
const findingIdToSlugAndFinding = (() => {
  const map = new Map<string, { slug: AntaresSlug; finding: AntaresFinding }>()
  for (const slug of ANTARES_SLUGS) {
    for (const finding of requireRun(slug).findings) {
      map.set(finding.finding_uuid, { slug, finding })
    }
  }
  return map
})()

// Triage results also live in a global map — the RC drawer looks up a single triage result by ID.
// We derive result IDs from the finding UUID so they are deterministic and easy to correlate.
const triageResultId = (findingId: string): string => `antares-triage-${findingId}`

const resultIdToSlugAndFinding = new Map<string, { slug: AntaresSlug; finding: AntaresFinding }>()
for (const [findingId, entry] of findingIdToSlugAndFinding.entries()) {
  resultIdToSlugAndFinding.set(triageResultId(findingId), entry)
}

export const isAntaresScanId = (id: string): boolean => SCAN_ID_TO_SLUG.has(id)
export const isAntaresRepositoryId = (id: string): boolean => REPO_ID_TO_SLUG.has(id)
export const isAntaresAnalysisId = (id: string): boolean => ANALYSIS_ID_TO_SLUG.has(id)
export const isAntaresFindingId = (id: string): boolean => findingIdToSlugAndFinding.has(id)
export const isAntaresTriageResultId = (id: string): boolean => resultIdToSlugAndFinding.has(id)

export const getAntaresScanIdBySlug = (slug: string): string => {
  invariant(isAntaresSlug(slug), `Unknown Antares slug: ${slug}`)
  return SCAN_ID_BY_SLUG[slug]
}

export const getAntaresRepositoryIdBySlug = (slug: string): string => {
  invariant(isAntaresSlug(slug), `Unknown Antares slug: ${slug}`)
  return REPO_ID_BY_SLUG[slug]
}

export const getAntaresAnalysisIdBySlug = (slug: string): string => {
  invariant(isAntaresSlug(slug), `Unknown Antares slug: ${slug}`)
  return ANALYSIS_ID_BY_SLUG[slug]
}

export const getAntaresSlugByScanId = (scanId: string): AntaresSlug | undefined => SCAN_ID_TO_SLUG.get(scanId)
export const getAntaresSlugByRepositoryId = (repoId: string): AntaresSlug | undefined => REPO_ID_TO_SLUG.get(repoId)
export const getAntaresSlugByAnalysisId = (analysisId: string): AntaresSlug | undefined =>
  ANALYSIS_ID_TO_SLUG.get(analysisId)

const isAntaresSlug = (slug: string): slug is AntaresSlug => (ANTARES_SLUGS as readonly string[]).includes(slug)

// The report keeps a leading "Improper Neutralization ... ('X')" style title. RC's finding table shows
// the raw title, which is fine — but we still trim it if it comes back empty for any reason.
const findingTitle = (finding: AntaresFinding): string => finding.title.trim() || finding.source_vuln_class

const severityFromLikelihood = (likelihood: string): Severity => {
  const normalized = likelihood.toLowerCase()
  if (normalized === 'high') {
    return { label: 'high', rank: 8 }
  }
  if (normalized === 'low') {
    return { label: 'low', rank: 2 }
  }
  // Anything else — Medium, empty, unknown — maps to medium.
  return { label: 'medium', rank: 5 }
}

const suggestedStatusFromResultType = (resultType: string): 'true_positive' | 'suspicious' | 'false_positive' => {
  if (resultType === 'TRUE_POSITIVE') {
    return 'true_positive'
  }
  if (resultType === 'FALSE_POSITIVE') {
    return 'false_positive'
  }
  // Everything else (UNCERTAIN, empty) surfaces as suspicious so the drawer shows a soft-yellow badge.
  return 'suspicious'
}

// The drawer's SUMMARY tab renders `outcome.summary` and the FULL REPORT tab renders the article
// endpoint separately — surfacing the full report in both tabs makes SUMMARY read like a duplicate.
// Use the short verdict for SUMMARY and let the article endpoint carry the long report.
const triageSummary = (finding: AntaresFinding): string => finding.triage?.tldr ?? ''

const scanIdForSlug = (slug: AntaresSlug): string => SCAN_ID_BY_SLUG[slug]
const analysisIdForSlug = (slug: AntaresSlug): string => ANALYSIS_ID_BY_SLUG[slug]

const findingLinks = (slug: AntaresSlug, findingId: string) => {
  const scanId = scanIdForSlug(slug)
  return {
    self: { href: `/api/v1/scans/${scanId}/findings/${findingId}` },
    results: { href: `/api/v1/scans/${scanId}/findings/${findingId}/results` },
    'representative-results': {
      href: `/api/v1/scans/${scanId}/findings/${findingId}/results?representative=true`,
    },
  }
}

const buildTriageResult = ({
  slug,
  finding,
}: {
  slug: AntaresSlug
  finding: AntaresFinding
}): FindingResultResponse => {
  const scanId = scanIdForSlug(slug)
  const resultId = triageResultId(finding.finding_uuid)
  const severity = severityFromLikelihood(finding.likelihood_of_exploit)
  const suggestedStatus = suggestedStatusFromResultType(finding.triage?.result_type ?? '')

  return FakeResult({
    id: resultId,
    type: 'triage',
    outcome: {
      type: 'completed-triage',
      suggested_status: suggestedStatus,
      suggested_severity: severity,
      summary: triageSummary(finding),
    },
    _links: {
      self: { href: `/api/v1/triage-results/${resultId}` },
      finding: { href: `/api/v1/scans/${scanId}/findings/${finding.finding_uuid}` },
      analysis: { href: `/api/v1/analyses/${analysisIdForSlug(slug)}` },
      article: { href: `/api/v1/triage-results/${resultId}/article` },
      report: { href: `/api/v1/triage-results/${resultId}/report` },
    },
  })
}

// Fix-result deterministic IDs — global uniqueness comes from the finding UUID.
const fixResultId = (findingId: string): string => `antares-fix-${findingId}`

const confidenceFromEffectiveness = (score: number | undefined): Confidence => {
  if (score === undefined) {
    return 'no-rating'
  }
  if (score >= 80) {
    return 'high'
  }
  if (score >= 50) {
    return 'medium'
  }
  if (score > 0) {
    return 'low'
  }
  return 'no-rating'
}

const strategyFromCodetf = (codetfResult: CodeTFFixResult): StrategyResponse => {
  const generation = codetfResult.fixMetadata?.generation
  if (generation?.provisional === true) {
    return 'provisional_ai'
  }
  if (generation?.strategy === 'hybrid') {
    return 'hybrid'
  }
  if (generation?.strategy === 'deterministic') {
    return 'deterministic'
  }
  return 'ai'
}

// The RatingResponse.description is a required non-empty string; fall back to the score when CodeTF
// omits the description so the schema still parses.
const ratingFromCodetf = (rating: CodeTFRating): RatingResponse => ({
  score: rating.score,
  description: rating.description ?? String(rating.score),
})

const qualityFromCodetf = (codetfResult: CodeTFFixResult): QualityResponse | undefined => {
  const quality = codetfResult.fixQuality
  if (!quality) {
    return undefined
  }
  return {
    safety: ratingFromCodetf(quality.safetyRating),
    effectiveness: ratingFromCodetf(quality.effectivenessRating),
    merge_readiness: ratingFromCodetf(quality.mergeReadinessRating),
    cleanliness: ratingFromCodetf(quality.cleanlinessRating),
  }
}

const fixOutcomeFromCodetf = (codetfResult: CodeTFFixResult): FixOutcome => {
  const status = codetfResult.fixStatus.status
  if (status === 'fixed') {
    return {
      type: 'completed-fix',
      summary: codetfResult.fixMetadata?.summary,
      description: codetfResult.fixMetadata?.description,
      strategy: strategyFromCodetf(codetfResult),
      confidence: confidenceFromEffectiveness(codetfResult.fixQuality?.effectivenessRating.score),
      quality: qualityFromCodetf(codetfResult),
    }
  }
  if (status === 'failed') {
    return {
      type: 'failed-fix',
      problem: {
        code: 'antares-fix-failed',
        message: codetfResult.fixStatus.reason ?? 'Fix generation failed',
      },
    }
  }
  if (status === 'blocked') {
    return {
      type: 'blocked-fix',
      reason: codetfResult.fixStatus.reason,
      details: codetfResult.fixStatus.details,
    }
  }
  if (status === 'wontfix') {
    return {
      type: 'blocked-fix',
      reason: 'wont fix',
      details: codetfResult.fixStatus.reason,
    }
  }
  if (status === 'excluded') {
    return {
      type: 'excluded-fix',
      reason: codetfResult.fixStatus.reason,
      details: codetfResult.fixStatus.details,
    }
  }
  return { type: 'no-recommendations-fix' }
}

const buildFixResult = ({
  slug,
  finding,
  codetfResult,
}: {
  slug: AntaresSlug
  finding: AntaresFinding
  codetfResult: CodeTFFixResult
}): FindingResultResponse => {
  const scanId = scanIdForSlug(slug)
  const resultId = fixResultId(finding.finding_uuid)
  return FakeResult({
    id: resultId,
    type: 'fix',
    outcome: fixOutcomeFromCodetf(codetfResult),
    _links: {
      self: { href: `/api/v1/fix-results/${resultId}` },
      finding: { href: `/api/v1/scans/${scanId}/findings/${finding.finding_uuid}` },
      analysis: { href: `/api/v1/analyses/${analysisIdForSlug(slug)}` },
      changesets: { href: `/api/v1/fix-results/${resultId}/changesets` },
      patches: { href: `/api/v1/fix-results/${resultId}/patches` },
      'latest-patch': { href: `/api/v1/fix-results/${resultId}/patches?latest=true` },
    },
  })
}

const changesetsFromCodetf = (changeSets: readonly CodeTFChangeSet[]): ChangesetPaginatedResponse => {
  const items = changeSets.map(changeSet => ({ path: changeSet.path, preview: changeSet.diff }))
  return {
    _links: {
      first: { href: '/api/v1/fix-results/antares/changesets' },
      self: { href: '/api/v1/fix-results/antares/changesets' },
    },
    total: items.length,
    page: { number: 0, size: items.length },
    _embedded: { items },
  }
}

// Fix result IDs → { slug, findingUuid, changeSets } lookup for the changesets handler.
// Populated eagerly during module init so per-request handlers can resolve without re-scanning CodeTF.
const fixResultIdToChangesets = new Map<string, ChangesetPaginatedResponse>()
const fixResultIdToSlugAndFinding = new Map<string, { slug: AntaresSlug; finding: AntaresFinding }>()

const collectFixEntriesForFinding = ({
  slug,
  finding,
}: {
  slug: AntaresSlug
  finding: AntaresFinding
}): CodeTFFixResult | undefined => {
  const codetfResult = getCodetfFixResultForFindingId(slug, finding.finding_uuid)
  if (!codetfResult) {
    return undefined
  }
  const resultId = fixResultId(finding.finding_uuid)
  fixResultIdToSlugAndFinding.set(resultId, { slug, finding })
  fixResultIdToChangesets.set(resultId, changesetsFromCodetf(codetfResult.changeSets))
  return codetfResult
}

// Prime the fix-result lookup maps up front so lookup helpers do not depend on when buildFinding
// runs. Iterating the catalog once at module init mirrors how the triage map above is populated.
for (const slug of ANTARES_SLUGS) {
  if (getCodetfForSlug(slug) === undefined) {
    continue
  }
  for (const finding of requireRun(slug).findings) {
    collectFixEntriesForFinding({ slug, finding })
  }
}

const buildFinding = ({ slug, finding }: { slug: AntaresSlug; finding: AntaresFinding }): FindingResponse => {
  const findingId = finding.finding_uuid
  const severity = severityFromLikelihood(finding.likelihood_of_exploit)
  const rule = `pixee_antares:${finding.cwe_ids.join(',')}`
  const links = findingLinks(slug, findingId)
  const triageResult = buildTriageResult({ slug, finding })

  const codetfResult = getCodetfFixResultForFindingId(slug, findingId)
  const fixResult = codetfResult ? buildFixResult({ slug, finding, codetfResult }) : undefined
  const items = fixResult ? [triageResult, fixResult] : [triageResult]

  return FakeFindingWithResults({
    id: findingId,
    title: findingTitle(finding),
    rule,
    severity,
    _links: links,
    _embedded: {
      'representative-results': {
        _links: {
          first: { href: links['representative-results'].href },
          self: { href: links['representative-results'].href },
          finding: { href: links.self.href },
        },
        _embedded: { items },
        page: { number: 0, size: items.length },
        total: items.length,
      },
    },
  })
}

// Fix counts derived from CodeTF entries the finding page will render. Findings without a matching
// CodeTF entry go into `no_recommendations` because the drawer treats absent fix results that way.
const fixBuckets = (slug: AntaresSlug, findings: readonly AntaresFinding[]) => {
  const status = { completed: 0, failed: 0, in_progress: 0, no_recommendations: 0 }
  const outcome = { blocked: 0, excluded: 0, has_fix: 0 }
  const confidence = { high: 0, low: 0, medium: 0, no_rating: 0 }
  for (const finding of findings) {
    const codetfResult = getCodetfFixResultForFindingId(slug, finding.finding_uuid)
    if (!codetfResult) {
      status.no_recommendations += 1
      confidence.no_rating += 1
      continue
    }
    const codetfStatus = codetfResult.fixStatus.status
    if (codetfStatus === 'fixed') {
      status.completed += 1
      outcome.has_fix += 1
      const bucket = confidenceFromEffectiveness(codetfResult.fixQuality?.effectivenessRating.score)
      if (bucket === 'high') {
        confidence.high += 1
      } else if (bucket === 'medium') {
        confidence.medium += 1
      } else if (bucket === 'low') {
        confidence.low += 1
      } else {
        confidence.no_rating += 1
      }
    } else if (codetfStatus === 'failed') {
      status.failed += 1
      confidence.no_rating += 1
    } else if (codetfStatus === 'blocked' || codetfStatus === 'wontfix') {
      status.completed += 1
      outcome.blocked += 1
      confidence.no_rating += 1
    } else if (codetfStatus === 'excluded') {
      status.completed += 1
      outcome.excluded += 1
      confidence.no_rating += 1
    } else {
      status.no_recommendations += 1
      confidence.no_rating += 1
    }
  }
  return { status, outcome, confidence }
}

// Suggested-status buckets in TriageOutcomeCounts. Our mapping only produces true_positive,
// false_positive, and inconclusive — every other bucket stays at zero for the demo scans.
const triageOutcomeBuckets = (findings: readonly AntaresFinding[]) => {
  const counts = {
    blocked: 0,
    excluded: 0,
    false_positive: 0,
    inconclusive: 0,
    likely_true_positive: 0,
    not_triaged: 0,
    true_positive: 0,
    wont_fix: 0,
  }
  for (const finding of findings) {
    const status = suggestedStatusFromResultType(finding.triage?.result_type ?? '')
    if (status === 'true_positive') {
      counts.true_positive += 1
    } else if (status === 'false_positive') {
      counts.false_positive += 1
    } else {
      // `suspicious` is a suggested-status; the closest aggregate bucket for uncertain / not-yet-classified
      // findings is `inconclusive`. See TriageOutcomeCountsSchema for the full list.
      counts.inconclusive += 1
    }
  }
  return counts
}

export const buildAntaresScanResponse = (slug: string): ScanResponse => {
  invariant(isAntaresSlug(slug), `Unknown Antares slug: ${slug}`)
  const report = requireRun(slug)
  const scanId = SCAN_ID_BY_SLUG[slug]
  const repoId = REPO_ID_BY_SLUG[slug]
  // Antares run reports may have a null git_head; fall back to a stable placeholder so the sha column
  // still renders instead of blowing up on a required string.
  const sha = report.git_head ?? '0000000000000000000000000000000000000000'
  const scannedAt = report.started_at

  return FakeScan({
    id: scanId,
    detector: 'antares',
    branch: 'main',
    sha,
    imported_at: scannedAt,
    scanned_at: scannedAt,
    html_url: undefined,
    _links: {
      self: { href: `/api/v1/scans/${scanId}` },
      findings: { href: `/api/v1/scans/${scanId}/findings` },
      patches: { href: `/api/v1/scans/${scanId}/patches` },
      repository: { href: `/api/v1/repositories/${repoId}`, title: `antares-demo/${slug}` },
      scale: { href: '/api/v1/scales/antares' },
      analyses: { href: `/api/v1/analyses?scan-ids=${scanId}` },
    },
  })
}

export const buildAntaresRepository = (slug: string): Repository => {
  invariant(isAntaresSlug(slug), `Unknown Antares slug: ${slug}`)
  const repoId = REPO_ID_BY_SLUG[slug]
  return FakeRepository({
    type: 'github',
    id: repoId,
    name: slug,
    full_name: `antares-demo/${slug}`,
    default_branch: 'main',
    owner: 'antares-demo',
    html_url: `https://github.com/antares-demo/${slug}`,
  })
}

export const buildAntaresAnalyses = (slug: string): AnalysisPaginatedResponse => {
  invariant(isAntaresSlug(slug), `Unknown Antares slug: ${slug}`)
  const analysis = buildAntaresAnalysis(slug)

  return {
    _links: {
      first: { href: `/api/v1/analyses?scan-ids=${scanIdForSlug(slug)}` },
      self: { href: `/api/v1/analyses?scan-ids=${scanIdForSlug(slug)}` },
    },
    total: 1,
    page: { number: 0, size: 10 },
    _embedded: { items: [analysis] },
  }
}

export const buildAntaresAnalysis = (slug: string): AnalysisResponse => {
  invariant(isAntaresSlug(slug), `Unknown Antares slug: ${slug}`)
  const scan = buildAntaresScanResponse(slug)
  const analysisId = analysisIdForSlug(slug)
  const scannedAt = requireRun(slug).started_at

  return FakeScanAnalysis({
    id: analysisId,
    current_state: { state: 'completed', timestamp: scannedAt },
    log: [
      { state: 'queued', timestamp: scannedAt },
      { state: 'in-progress', timestamp: scannedAt },
      { state: 'completed', timestamp: scannedAt },
    ],
    _links: {
      self: { href: `/api/v1/analyses/${analysisId}` },
      scan: { href: `/api/v1/scans/${scanIdForSlug(slug)}` },
    },
    _embedded: { scan },
  })
}

export type BuildAntaresFindingsOptions = {
  pageNumber?: number
  pageSize?: number
}

export const buildAntaresFindings = (
  slug: string,
  opts: BuildAntaresFindingsOptions = {}
): FindingAggregateResponse => {
  invariant(isAntaresSlug(slug), `Unknown Antares slug: ${slug}`)
  const report = requireRun(slug)
  const pageNumber = opts.pageNumber ?? 0
  const pageSize = opts.pageSize ?? 25

  // Demo-hidden: findings whose CodeTF fix result failed (e.g. "fix engine timeout") are dropped
  // from the table entirely so the click-through carries no half-baked outcomes.
  const keptFindings = report.findings.filter(finding => {
    const codetf = getCodetfFixResultForFindingId(slug, finding.finding_uuid)
    return codetf?.fixStatus.status !== 'failed'
  })
  const allFindings = keptFindings.map(finding => buildFinding({ slug, finding }))
  const start = pageNumber * pageSize
  const items = allFindings.slice(start, start + pageSize)
  const total = allFindings.length
  const outcomeBuckets = triageOutcomeBuckets(keptFindings)
  const fix = fixBuckets(slug, keptFindings)
  const scanId = scanIdForSlug(slug)

  return FakePaginatedFindingsWithResults({
    _links: {
      first: { href: `/api/v1/scans/${scanId}/findings` },
      self: { href: `/api/v1/scans/${scanId}/findings` },
    },
    total,
    page: { number: pageNumber, size: pageSize },
    _embedded: { items },
    completed_analysis: total,
    in_progress_analysis: 0,
    triage: {
      status: {
        completed: total,
        failed: 0,
        in_progress: 0,
        no_recommendations: 0,
      },
      outcome: outcomeBuckets,
    },
    fix,
    // No SCA / composite outputs for Antares demo scans.
    sca: {
      status: { completed: 0, failed: 0, blocked: 0, excluded: 0, in_progress: 0 },
      classification: { exploitable: 0, not_exploitable: 0, inconclusive: 0 },
    },
    composite: { ready_to_fix: 0, no_fix_necessary: 0 },
  })
}

export const buildAntaresFindingById = (findingId: string): FindingResponse | undefined => {
  const entry = findingIdToSlugAndFinding.get(findingId)
  if (!entry) {
    return undefined
  }
  return buildFinding(entry)
}

export const buildAntaresTriageResultsForFinding = (
  findingId: string
): { representative: FindingResultResponse[]; all: FindingResultResponse[] } | undefined => {
  const entry = findingIdToSlugAndFinding.get(findingId)
  if (!entry) {
    return undefined
  }
  const triageResult = buildTriageResult(entry)
  const codetfResult = getCodetfFixResultForFindingId(entry.slug, findingId)
  const items = codetfResult
    ? [triageResult, buildFixResult({ slug: entry.slug, finding: entry.finding, codetfResult })]
    : [triageResult]
  return { representative: items, all: items }
}

// The SUMMARY tab renders TriageArticle from this endpoint — that's the short executive verdict,
// not the long report. FULL REPORT reads getAntaresTriageReportByResultId below.
export const getAntaresTriageArticleByResultId = (resultId: string): string | undefined => {
  const entry = resultIdToSlugAndFinding.get(resultId)
  if (!entry) {
    return undefined
  }
  return entry.finding.triage?.tldr
}

export const getAntaresTriageReportByResultId = (resultId: string): string | undefined => {
  const entry = resultIdToSlugAndFinding.get(resultId)
  if (!entry) {
    return undefined
  }
  return entry.finding.triage?.full_report
}

export const isAntaresFixResultId = (resultId: string): boolean => fixResultIdToSlugAndFinding.has(resultId)

export const getAntaresFixResultChangesets = (resultId: string): ChangesetPaginatedResponse | undefined =>
  fixResultIdToChangesets.get(resultId)

// =============================================
// Repository-level analyses (threat-model / finding-overlays / scorecards)
//
// The Antares-generated envelopes use snake_case and carry extra bookkeeping fields (id,
// repository_id, created_at, completed_at, threat_model_doc_id) that the platform schemas do not
// declare. Each builder below shims to the platform's camelCase envelope, coerces the analysis-
// service `phase: "completed"` to whichever terminal phase-enum value the platform picks for a
// finished run, and rewrites HAL `_links` to internally-consistent placeholders anchored at
// `/api/v1/repositories/antares-repo-<slug>/`. Missing files return undefined so callers can fall
// back to the platform's default fake fixture.

type SnakeThreatModelEnvelope = {
  commit_sha?: string
  completed_at?: string
  created_at?: string
  failure_reason?: string | null
  phase: string
  requested_by?: string
  status: string
  _links?: Record<string, { href: string }>
}

type SnakeFindingOverlaySummary = {
  cluster_count: number
  highest_tier?: string
  priority_count: number
}

type SnakeFindingOverlayEnvelope = {
  completed_at?: string
  created_at?: string
  failure_reason?: string | null
  phase: string
  requested_by?: string
  status: string
  summary?: SnakeFindingOverlaySummary
  _links?: Record<string, { href: string }>
}

type SnakeFindingOverlayPriority = {
  finding_id: string
  rationale?: string
  score: number
  tier: string
}

type SnakeFindingOverlayCluster = {
  dimension: string
  finding_ids: string[]
  name: string
  rationale?: string
  subject_name: string
}

type SnakeFindingOverlayContent = {
  assumptions?: string[]
  clusters: SnakeFindingOverlayCluster[]
  narrative: string
  priorities: SnakeFindingOverlayPriority[]
}

type SnakeDimensionEvidence = {
  excerpt?: string | null
  location: string
  note?: string | null
}

type SnakeDimensionAnswer = {
  answer: string
  evidence: SnakeDimensionEvidence[]
  id: string
  question: string
  rationale?: string
}

type SnakeDimensionScore = {
  answers: SnakeDimensionAnswer[]
  confidence: number
  description: string
  dimension: string
  display_name: string
  generated_at: string
  model_used: string
  rationale: string
  rubric_version: string
  score: number
}

type SnakeScorecardPayload = {
  assumptions_overhang: number
  commit_sha: string
  dimensions: SnakeDimensionScore[]
  generated_at: string
  system_overview: string
  threat_model_commit_sha: string
  threat_model_doc_id: string
}

type SnakeScorecardEnvelope = {
  commit_sha?: string
  completed_at?: string
  created_at?: string
  failure_reason?: string | null
  phase: string
  requested_by?: string
  scorecard?: SnakeScorecardPayload
  status: string
  _links?: Record<string, { href: string }>
}

const repositoryHrefPrefix = (slug: AntaresSlug): string => `/api/v1/repositories/${REPO_ID_BY_SLUG[slug]}`

const generatedAtFrom = (envelope: { completed_at?: string; created_at?: string }): string | undefined =>
  envelope.completed_at ?? envelope.created_at

const nullToUndefined = (value: string | null | undefined): string | undefined => value ?? undefined

// Platform status/phase enums are wider than the analysis-service reports. When status is a
// non-terminal value we defer to whatever string analysis-service sent (Zod treats unknown values
// as validation errors, but the fixtures are always terminal). For phase, analysis-service sends
// `"completed"` on finished runs, but every platform phase enum tops out at the last in-progress
// step — we map to that step so the schema accepts the response.
const terminalThreatModelPhase = 'rendering'
const terminalFindingOverlayPhase = 'analyzing'
const terminalScorecardsPhase = 'finalizing'

const normalizeThreatModelPhase = (phase: string): ThreatModelResponse['phase'] => {
  if (phase === 'completed') {
    return terminalThreatModelPhase
  }
  if (
    phase === 'queued' ||
    phase === 'fetching_code' ||
    phase === 'exploring_codebase' ||
    phase === 'modeling_threats' ||
    phase === 'rendering'
  ) {
    return phase
  }
  return 'unknown'
}

const normalizeFindingOverlayPhase = (phase: string): FindingOverlaysResponse['phase'] => {
  if (phase === 'completed' || phase === 'analyzing') {
    return terminalFindingOverlayPhase
  }
  if (phase === 'queued') {
    return 'queued'
  }
  return 'unknown'
}

const normalizeScorecardsPhase = (phase: string): ScorecardsResponse['phase'] => {
  if (phase === 'completed') {
    return terminalScorecardsPhase
  }
  if (
    phase === 'queued' ||
    phase === 'fetching_code' ||
    phase === 'loading_threat_model' ||
    phase === 'scoring_dimensions' ||
    phase === 'finalizing'
  ) {
    return phase
  }
  return terminalScorecardsPhase
}

const normalizeThreatModelStatus = (status: string): ThreatModelResponse['status'] => {
  if (status === 'processing' || status === 'completed' || status === 'failed') {
    return status
  }
  return 'unknown'
}

const normalizeFindingOverlayStatus = (status: string): FindingOverlaysResponse['status'] => {
  if (status === 'processing' || status === 'completed' || status === 'failed') {
    return status
  }
  return 'unknown'
}

const normalizeScorecardsStatus = (status: string): ScorecardsResponse['status'] => {
  if (status === 'processing' || status === 'completed' || status === 'failed') {
    return status
  }
  return 'completed'
}

const knownTiers = ['critical', 'high', 'medium', 'low', 'unknown'] as const
const knownDimensions = [
  'entry_point',
  'actor',
  'trust_boundary',
  'sensitive_asset',
  'vulnerability_class',
  'unknown',
] as const

const isKnownTier = (tier: string): tier is (typeof knownTiers)[number] =>
  (knownTiers as readonly string[]).includes(tier)

const isKnownDimension = (dimension: string): dimension is (typeof knownDimensions)[number] =>
  (knownDimensions as readonly string[]).includes(dimension)

const normalizeTier = (tier: string): FindingOverlayPriority['tier'] => {
  if (isKnownTier(tier)) {
    return tier
  }
  return 'unknown'
}

const normalizeDimension = (dimension: string): FindingOverlayCluster['dimension'] => {
  if (isKnownDimension(dimension)) {
    return dimension
  }
  return 'unknown'
}

const normalizeAnswer = (answer: string): DimensionAnswer['answer'] => {
  if (answer === 'yes' || answer === 'no' || answer === 'na') {
    return answer
  }
  return 'na'
}

const buildEvidence = (evidence: SnakeDimensionEvidence): DimensionAnswerEvidence => ({
  excerpt: nullToUndefined(evidence.excerpt),
  location: evidence.location,
  note: nullToUndefined(evidence.note),
})

const buildAnswer = (answer: SnakeDimensionAnswer): DimensionAnswer => ({
  answer: normalizeAnswer(answer.answer),
  evidence: answer.evidence.map(buildEvidence),
  id: answer.id,
  question: answer.question,
  rationale: answer.rationale,
})

const buildDimensionScore = (dimension: SnakeDimensionScore): DimensionScore => ({
  answers: dimension.answers.map(buildAnswer),
  confidence: dimension.confidence,
  description: dimension.description,
  dimension: dimension.dimension,
  displayName: dimension.display_name,
  generatedAt: dimension.generated_at,
  modelUsed: dimension.model_used,
  rationale: dimension.rationale,
  rubricVersion: dimension.rubric_version,
  score: dimension.score,
})

const buildScorecardPayload = (payload: SnakeScorecardPayload): ScorecardPayload => ({
  assumptionsOverhang: payload.assumptions_overhang,
  commitSha: payload.commit_sha,
  dimensions: payload.dimensions.map(buildDimensionScore),
  generatedAt: payload.generated_at,
  systemOverview: payload.system_overview,
  threatModelCommitSha: payload.threat_model_commit_sha,
  threatModelDocId: payload.threat_model_doc_id,
})

const buildPriority = (priority: SnakeFindingOverlayPriority): FindingOverlayPriority => ({
  findingId: priority.finding_id,
  rationale: priority.rationale,
  score: priority.score,
  tier: normalizeTier(priority.tier),
})

const buildCluster = (cluster: SnakeFindingOverlayCluster): FindingOverlayCluster => ({
  dimension: normalizeDimension(cluster.dimension),
  findings: cluster.finding_ids.map(findingId => ({ findingId })),
  name: cluster.name,
  rationale: cluster.rationale,
  subjectName: cluster.subject_name,
})

// analysis-service's `highest_tier` is a wider free-form string; if it does not match a platform
// tier enum value, drop the field so the summary omits it rather than failing schema validation.
const normalizeHighestTier = (tier: string | undefined): FindingOverlayPriority['tier'] | undefined => {
  if (tier === undefined) {
    return undefined
  }
  if (isKnownTier(tier)) {
    return tier
  }
  return undefined
}

const threatModelLinks = (slug: AntaresSlug): ThreatModelResponse['_links'] => {
  const prefix = repositoryHrefPrefix(slug)
  return {
    self: { href: `${prefix}/threat-model` },
    content: { href: `${prefix}/threat-model/content` },
    events: { href: `${prefix}/threat-model/events` },
  }
}

const findingOverlaysLinks = (slug: AntaresSlug): FindingOverlaysResponse['_links'] => {
  const prefix = repositoryHrefPrefix(slug)
  return {
    self: { href: `${prefix}/finding-overlays` },
    content: { href: `${prefix}/finding-overlays/content` },
    events: { href: `${prefix}/finding-overlays/events` },
  }
}

const scorecardsLinks = (slug: AntaresSlug): ScorecardsResponse['_links'] => ({
  self: { href: `${repositoryHrefPrefix(slug)}/scorecards` },
})

// Raw JSON from disk is `unknown` at the module boundary. Each builder narrows through a snake-case
// intermediate type so the shim reads as an ordinary transform. Static JSON does not carry a nominal
// TypeScript type, so the cast at the seam is unavoidable — the same pattern as CodeTF above.
const asSnakeThreatModelEnvelope = (raw: unknown): SnakeThreatModelEnvelope => raw as SnakeThreatModelEnvelope
const asSnakeFindingOverlayEnvelope = (raw: unknown): SnakeFindingOverlayEnvelope => raw as SnakeFindingOverlayEnvelope
const asSnakeFindingOverlayContent = (raw: unknown): SnakeFindingOverlayContent => raw as SnakeFindingOverlayContent
const asSnakeScorecardEnvelope = (raw: unknown): SnakeScorecardEnvelope => raw as SnakeScorecardEnvelope
const asThreatModelContent = (raw: unknown): ThreatModelContent => raw as ThreatModelContent

export const buildAntaresThreatModel = (slug: string): ThreatModelResponse | undefined => {
  if (!isAntaresSlug(slug)) {
    return undefined
  }
  const raw = getAntaresThreatModelForSlug(slug)
  if (raw === undefined) {
    return undefined
  }
  const envelope = asSnakeThreatModelEnvelope(raw)
  return {
    _links: threatModelLinks(slug),
    commitSha: envelope.commit_sha,
    failureReason: nullToUndefined(envelope.failure_reason),
    generatedAt: generatedAtFrom(envelope),
    phase: normalizeThreatModelPhase(envelope.phase),
    requestedBy: envelope.requested_by,
    status: normalizeThreatModelStatus(envelope.status),
  }
}

export const buildAntaresThreatModelContent = (slug: string): ThreatModelContent | undefined => {
  if (!isAntaresSlug(slug)) {
    return undefined
  }
  const raw = getAntaresThreatModelContentForSlug(slug)
  if (raw === undefined) {
    return undefined
  }
  return asThreatModelContent(raw)
}

export const buildAntaresScorecards = (slug: string): ScorecardsResponse | undefined => {
  if (!isAntaresSlug(slug)) {
    return undefined
  }
  const raw = getAntaresScorecardForSlug(slug)
  if (raw === undefined) {
    return undefined
  }
  const envelope = asSnakeScorecardEnvelope(raw)
  return {
    _links: scorecardsLinks(slug),
    commitSha: envelope.commit_sha,
    failureReason: nullToUndefined(envelope.failure_reason),
    generatedAt: generatedAtFrom(envelope),
    phase: normalizeScorecardsPhase(envelope.phase),
    requestedBy: envelope.requested_by,
    scorecard: envelope.scorecard ? buildScorecardPayload(envelope.scorecard) : undefined,
    status: normalizeScorecardsStatus(envelope.status),
  }
}

export const buildAntaresFindingOverlays = (slug: string): FindingOverlaysResponse | undefined => {
  if (!isAntaresSlug(slug)) {
    return undefined
  }
  const raw = getAntaresFindingOverlayForSlug(slug)
  if (raw === undefined) {
    return undefined
  }
  const envelope = asSnakeFindingOverlayEnvelope(raw)
  const summary = envelope.summary
  return {
    _links: findingOverlaysLinks(slug),
    failureReason: nullToUndefined(envelope.failure_reason),
    generatedAt: generatedAtFrom(envelope),
    phase: normalizeFindingOverlayPhase(envelope.phase),
    requestedBy: envelope.requested_by,
    status: normalizeFindingOverlayStatus(envelope.status),
    summary: summary
      ? {
          clusterCount: summary.cluster_count,
          highestTier: normalizeHighestTier(summary.highest_tier),
          priorityCount: summary.priority_count,
        }
      : undefined,
  }
}

export const buildAntaresFindingOverlayContent = (slug: string): FindingOverlayResponse | undefined => {
  if (!isAntaresSlug(slug)) {
    return undefined
  }
  const raw = getAntaresFindingOverlayContentForSlug(slug)
  if (raw === undefined) {
    return undefined
  }
  const content = asSnakeFindingOverlayContent(raw)
  return {
    assumptions: content.assumptions ?? [],
    clusters: content.clusters.map(buildCluster),
    narrative: content.narrative,
    priorities: content.priorities.map(buildPriority),
  }
}

// Backlink alias — the `For`/`By` naming is inconsistent across the codebase, and both variants
// resolve to the same slug lookup. Handlers that already imported `getAntaresSlugByRepositoryId`
// keep working; new callers can use either name.
export const getAntaresSlugForRepositoryId = (repositoryId: string): AntaresSlug | undefined =>
  getAntaresSlugByRepositoryId(repositoryId)
